import { memo, useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as ChartTooltip,
} from 'recharts';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, defaultPalette, currencyFmt } from './shared';
import type { FieldBreakdownDashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: FieldBreakdownDashboardWidget;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

function FieldBreakdownWidgetImpl({ widget, sourceId, parameters }: Props) {
  const { data, isLoading } = useWidgetData(
    sourceId,
    `field-breakdown-${widget.reportCode}`,
    parameters,
  );
  const { title, subtitle, fields } = widget.config;

  const series = useMemo(() => {
    const rows = data ?? [];
    return fields
      .map((f) => ({
        name: f.label,
        value: rows.reduce((acc, r) => acc + (Number(r[f.field]) || 0), 0),
        color: f.color,
      }))
      .filter((s) => s.value > 0);
  }, [data, fields]);

  return (
    <div data-testid="field-breakdown-widget">
      <Panel title={title} subtitle={subtitle}>
        {isLoading ? (
          <Skeleton.Node active style={{ width: '100%', height: 260 }} />
        ) : series.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={series}
                dataKey="value"
                nameKey="name"
                innerRadius={54}
                outerRadius={82}
                paddingAngle={1}
              >
                {series.map((s, i) => (
                  <Cell key={i} fill={s.color ?? defaultPalette[i % defaultPalette.length]} />
                ))}
              </Pie>
              <ChartTooltip
                formatter={(v) => currencyFmt.format(Number(v))}
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid var(--color-border)',
                  fontSize: 12,
                }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Panel>
    </div>
  );
}

export const FieldBreakdownWidget = memo(FieldBreakdownWidgetImpl);
